import { useState, useEffect, useCallback } from 'react';
import API from '../Api';

// In-memory cache for settings
let settingsCache = null;
let settingsCacheTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Custom hook to fetch and cache application settings
 * Prevents duplicate API calls across components
 * @returns {Object} { settings, loading, error, refetch }
 */
const useSettings = () => {
	const [settings, setSettings] = useState(settingsCache || {});
	const [loading, setLoading] = useState(!settingsCache);
	const [error, setError] = useState(null);

	const fetchSettings = useCallback(async () => {
		// Check if cache is still valid
		if (settingsCache && settingsCacheTime) {
			const now = Date.now();
			if (now - settingsCacheTime < CACHE_DURATION) {
				setSettings(settingsCache);
				setLoading(false);
				return settingsCache;
			}
		}

		setLoading(true);
		setError(null);

		try {
			const res = await API.get('setting/get');
			const settingsData = res.data.data[0] || res.data.data || {};

			// Update cache
			settingsCache = settingsData;
			settingsCacheTime = Date.now();

			setSettings(settingsData);
			setLoading(false);
			return settingsData;
		} catch (err) {
			setError(err);
			setLoading(false);

			// Still return cached data if available on error
			if (settingsCache) {
				setSettings(settingsCache);
				return settingsCache;
			}
			return {};
		}
	}, []);

	useEffect(() => {
		fetchSettings();
	}, [fetchSettings]);

	// Manual refetch function to invalidate cache
	const refetch = useCallback(async () => {
		settingsCache = null;
		settingsCacheTime = null;
		return await fetchSettings();
	}, [fetchSettings]);

	return {
		settings,
		loading,
		error,
		refetch
	};
};

// Export function to clear cache (useful for testing or manual invalidation)
export const clearSettingsCache = () => {
	settingsCache = null;
	settingsCacheTime = null;
	
};

export default useSettings;
